import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import {
  getEvolution,
  getNetworkOverview,
  getRejectionReasons,
} from '../../../../../../api/theHive/redeProto';
import {
  EvolutionPoint,
  Pillar,
} from '../../../../../../api/theHive/redeProto/types';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import useSnackbar from '../../../../../../hooks/useSnackbar';
import { RedeProtoOutletContext } from '../../types';
import { composeBsc } from '../../utils/composeBsc';

interface UseBscHook {
  pillars: Pillar[];
  evolution: EvolutionPoint[];
  loading: boolean;
  error: boolean;
}

export default function useBsc(): UseBscHook {
  const [pillars, setPillars] = useState<Pillar[]>([]);
  const [evolution, setEvolution] = useState<EvolutionPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const { getErrorMessage } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();
  const { selectedNoticeId } = useOutletContext<RedeProtoOutletContext>();

  const getData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [overviewRes, evolutionRes, reasonsRes] = await Promise.all([
        getNetworkOverview(selectedNoticeId),
        getEvolution(undefined, selectedNoticeId),
        getRejectionReasons(selectedNoticeId),
      ]);

      [overviewRes, evolutionRes, reasonsRes].forEach((response) => {
        if (response.detail.description) {
          throw new Error(response.detail.description);
        }
        if (!response.data) {
          throw new Error('Algo deu errado, tente novamente.');
        }
      });

      const evolutionData = evolutionRes.data ?? [];
      setEvolution(evolutionData);
      setPillars(
        composeBsc(overviewRes.data, evolutionData, reasonsRes.data ?? [])
      );
    } catch (requestError) {
      setError(true);
      handleSnackbar(getErrorMessage(requestError), true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNoticeId]);

  useEffect(() => {
    getData();
  }, [getData]);

  return { pillars, evolution, loading, error };
}
